// Shared PDF generator for consistent exports across the app
// This is the ONE source of truth for all PDF exports in the system

import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { finalHoleDiameterOptions } from './complexityCalculator';
import { defaultSettings } from '../state/settingsState';

const SETTINGS_STORAGE_KEY = 'complexityIndexSettings';

// Load settings from localStorage or use defaults
const getSettings = () => {
  try {
    const stored = localStorage.getItem(SETTINGS_STORAGE_KEY);
    if (stored) {
      return { ...defaultSettings, ...JSON.parse(stored) };
    }
  } catch (e) {
    console.warn('Failed to load settings for PDF');
  }
  return defaultSettings;
};

const getComplexityClass = (score) => {
  if (score < 33) return 'low';
  if (score < 66) return 'moderate';
  return 'high';
};

const getComplexityLabel = (score) => {
  if (score < 33) return 'Low';
  if (score < 66) return 'Moderate';
  return 'High';
};

// Translate raw values to display values using settings
const getDisplayValue = (key, value, settings) => {
  const lookups = {
    location: settings.locationValues,
    rigCapacity: settings.rigCapacityValues,
    circulatingTemp: settings.circulationValues,
    pressureRegime: settings.pressRegimeValues,
    ucs: settings.ucsValues,
    ddi: settings.ddiValues,
    phase: settings.phaseValues
  };

  if (lookups[key] && lookups[key][value]) {
    return lookups[key][value].txt;
  }

  if (key === 'finalHoleDiameter') {
    const opt = finalHoleDiameterOptions.find(o => o.value === value);
    return opt ? opt.label : value;
  }

  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (value === null || value === undefined || value === '') return '—';

  return value;
};

const formatBoolean = (value) => value ? 'Yes' : 'No';

export const generatePDFContent = ({ name, scores, formData, units = 'metric' }) => {
  const settings = getSettings();
  const totalScore = Math.round(((scores.siteScore || 0) + (scores.wellScore || 0)) / 2 * 100);
  const siteScore = Math.round((scores.siteScore || 0) * 100);
  const wellScore = Math.round((scores.wellScore || 0) * 100);
  const dateStr = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  const complexityClass = getComplexityClass(totalScore);
  const complexityLabel = getComplexityLabel(totalScore);

  const depthUnit = units === 'metric' ? 'm' : 'ft';
  const tempUnit = units === 'metric' ? '°C' : '°F';
  const pressUnit = units === 'metric' ? 'bar' : 'psi';

  // Helper to get display values using settings
  const dv = (key, value) => getDisplayValue(key, value, settings);

  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>Complexity Index - ${name}</title>
  <link rel="preconnect" href="https://fonts.googleapis.com">
  <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
  <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap" rel="stylesheet">
  <style>
    @page { size: A4; margin: 0; }
    * { margin: 0; padding: 0; box-sizing: border-box; }
    html, body {
      width: 100%;
      height: 100%;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
    body {
      font-family: 'Inter', -apple-system, sans-serif;
      font-size: 11px;
      color: #18181b;
      background: white;
      padding: 40px;
    }
    .page {
      width: 100%;
      min-height: 100%;
    }

    /* Header - using table for html2canvas compatibility */
    .header {
      display: table;
      width: 100%;
      padding-bottom: 16px;
      border-bottom: 3px solid #18181b;
      margin-bottom: 24px;
    }
    .header-left, .header-right {
      display: table-cell;
      vertical-align: middle;
    }
    .header-right { text-align: right; }
    .logo { display: inline-block; }
    .logo-mark {
      display: inline-block;
      background: #d62828;
      color: white;
      font-weight: 800;
      font-size: 16px;
      padding: 8px 10px;
      vertical-align: middle;
    }
    .logo-text {
      display: inline-block;
      font-size: 14px;
      font-weight: 500;
      color: #52525b;
      vertical-align: middle;
      margin-left: 12px;
    }
    .doc-info { font-size: 12px; color: #71717a; }
    .doc-info strong { display: block; color: #18181b; font-weight: 600; font-size: 14px; }

    /* Hero - using table layout */
    .hero {
      display: table;
      width: 100%;
      margin-bottom: 28px;
    }
    .score-panel {
      display: table-cell;
      background: #18181b;
      color: white;
      padding: 24px;
      width: 200px;
      vertical-align: top;
    }
    .score-label {
      font-size: 10px;
      text-transform: uppercase;
      letter-spacing: 1.5px;
      color: #a1a1aa;
      margin-bottom: 8px;
    }
    .score-value {
      font-size: 64px;
      font-weight: 800;
      line-height: 1;
      letter-spacing: -3px;
    }
    .score-value span { font-size: 24px; font-weight: 500; }
    .complexity-badge {
      display: inline-block;
      margin-top: 12px;
      padding: 4px 10px;
      font-size: 10px;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 1px;
    }
    .complexity-badge.low { background: #16a34a; }
    .complexity-badge.moderate { background: #ca8a04; }
    .complexity-badge.high { background: #dc2626; }

    .hero-right {
      display: table-cell;
      vertical-align: middle;
      padding-left: 24px;
    }
    .assessment-title {
      font-size: 28px;
      font-weight: 300;
      color: #18181b;
      margin-bottom: 16px;
      line-height: 1.2;
    }
    .level-bars { width: 100%; }
    .bar-row {
      display: table;
      width: 100%;
      margin-bottom: 12px;
    }
    .bar-label {
      display: table-cell;
      width: 80px;
      font-size: 12px;
      color: #52525b;
      vertical-align: middle;
    }
    .bar-track {
      display: table-cell;
      height: 10px;
      background: #f4f4f5;
      vertical-align: middle;
    }
    .bar-fill { height: 10px; background: #18181b; }
    .bar-value {
      display: table-cell;
      width: 60px;
      text-align: right;
      font-size: 14px;
      font-weight: 700;
      vertical-align: middle;
      padding-left: 12px;
    }

    .section-title {
      font-size: 11px;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 1.5px;
      color: #d62828;
      padding-bottom: 8px;
      border-bottom: 2px solid #e4e4e7;
      margin-bottom: 12px;
    }

    /* Breakdown - using table layout */
    .breakdown-grid {
      display: table;
      width: 100%;
      table-layout: fixed;
      margin-bottom: 28px;
    }
    .breakdown-item {
      display: table-cell;
      background: #fafafa;
      padding: 16px 8px;
      text-align: center;
      width: 16.666%;
    }
    .breakdown-item + .breakdown-item {
      border-left: 12px solid white;
    }
    .breakdown-value {
      font-size: 28px;
      font-weight: 800;
      color: #18181b;
      line-height: 1;
    }
    .breakdown-label {
      font-size: 10px;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      color: #71717a;
      margin-top: 8px;
    }

    .params-section { margin-bottom: 20px; }
    /* Params - using table layout */
    .params-grid {
      display: table;
      width: 100%;
      table-layout: fixed;
    }
    .params-grid > div {
      display: table-cell;
      width: 25%;
      padding-right: 20px;
      vertical-align: top;
    }
    .params-grid > div:last-child {
      padding-right: 0;
    }
    .param-group-title {
      font-size: 10px;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 1px;
      color: #a1a1aa;
      margin-bottom: 8px;
      padding-bottom: 4px;
      border-bottom: 1px solid #e4e4e7;
    }
    .param-row {
      display: table;
      width: 100%;
      padding: 4px 0;
      border-bottom: 1px solid #f4f4f5;
      font-size: 11px;
    }
    .param-label {
      display: table-cell;
      color: #71717a;
    }
    .param-value {
      display: table-cell;
      font-weight: 600;
      color: #18181b;
      text-align: right;
    }

    /* Footer - using table layout */
    .footer {
      margin-top: 40px;
      padding-top: 16px;
      border-top: 2px solid #e4e4e7;
      display: table;
      width: 100%;
      font-size: 11px;
      color: #a1a1aa;
    }
    .footer-left, .footer-right {
      display: table-cell;
    }
    .footer-right { text-align: right; }
    .footer strong { color: #52525b; }
  </style>
</head>
<body>
  <div class="page">
    <div class="header">
      <div class="header-left">
        <div class="logo">
          <span class="logo-mark">IADC</span>
          <span class="logo-text">Geothermal Well Complexity Index</span>
        </div>
      </div>
      <div class="header-right">
        <div class="doc-info">
          <strong>Assessment Report</strong>
          ${dateStr}
        </div>
      </div>
    </div>

    <div class="hero">
      <div class="score-panel">
        <div class="score-label">Overall Score</div>
        <div class="score-value">${totalScore}<span> %</span></div>
        <div class="complexity-badge ${complexityClass}">${complexityLabel} Complexity</div>
      </div>
      <div class="hero-right">
        <div class="assessment-title">${name}</div>
        <div class="level-bars">
          <div class="bar-row">
            <span class="bar-label">Site Level</span>
            <div class="bar-track"><div class="bar-fill" style="width: ${siteScore}%"></div></div>
            <span class="bar-value">${siteScore} %</span>
          </div>
          <div class="bar-row">
            <span class="bar-label">Well Level</span>
            <div class="bar-track"><div class="bar-fill" style="width: ${wellScore}%"></div></div>
            <span class="bar-value">${wellScore} %</span>
          </div>
        </div>
      </div>
    </div>

    <div class="section-title">Component Breakdown</div>
    <div class="breakdown-grid">
      <div class="breakdown-item">
        <div class="breakdown-value">${Math.round((scores.locationScore || 0) * 100)}</div>
        <div class="breakdown-label">Location</div>
      </div>
      <div class="breakdown-item">
        <div class="breakdown-value">${Math.round((scores.rigScore || 0) * 100)}</div>
        <div class="breakdown-label">Rig Capacity</div>
      </div>
      <div class="breakdown-item">
        <div class="breakdown-value">${Math.round((scores.dsgnScore || 0) * 100)}</div>
        <div class="breakdown-label">Design</div>
      </div>
      <div class="breakdown-item">
        <div class="breakdown-value">${Math.round((scores.cnstrctnScore || 0) * 100)}</div>
        <div class="breakdown-label">Construction</div>
      </div>
      <div class="breakdown-item">
        <div class="breakdown-value">${Math.round((scores.drlgCmplxScore || 0) * 100)}</div>
        <div class="breakdown-label">Drilling</div>
      </div>
      <div class="breakdown-item">
        <div class="breakdown-value">${Math.round((scores.wllCtrlScore || 0) * 100)}</div>
        <div class="breakdown-label">Well Control</div>
      </div>
    </div>

    <div class="params-section">
      <div class="section-title">Input Parameters</div>
      <div class="params-grid">
        <div>
          <div class="param-group-title">Site Level</div>
          <div class="param-row"><span class="param-label">Location</span><span class="param-value">${dv('location', formData.location)}</span></div>
          <div class="param-row"><span class="param-label">Residential</span><span class="param-value">${formatBoolean(formData.residential)}</span></div>
          <div class="param-row"><span class="param-label">Sensitive</span><span class="param-value">${formatBoolean(formData.sensitive)}</span></div>
          <div class="param-row"><span class="param-label">Rig Capacity</span><span class="param-value">${dv('rigCapacity', formData.rigCapacity)}</span></div>
        </div>
        <div>
          <div class="param-group-title">Design</div>
          <div class="param-row"><span class="param-label">Re-Entry</span><span class="param-value">${formatBoolean(formData.reEntry)}</span></div>
          <div class="param-row"><span class="param-label">Function</span><span class="param-value">${formData.Function || '—'}</span></div>
          <div class="param-row"><span class="param-label">Hole Dia.</span><span class="param-value">${dv('finalHoleDiameter', formData.finalHoleDiameter)}</span></div>
          <div class="param-row"><span class="param-label">Sections</span><span class="param-value">${formData.numberOfSections || '—'}</span></div>
          <div class="param-row"><span class="param-label">Depth</span><span class="param-value">${formData.verticalDepth || '—'} ${depthUnit}</span></div>
          <div class="param-row"><span class="param-label">Max Temp</span><span class="param-value">${formData.maxTemperature || '—'}${tempUnit}</span></div>
          <div class="param-row"><span class="param-label">Max Press.</span><span class="param-value">${formData.maxPressure || '—'} ${pressUnit}</span></div>
          <div class="param-row"><span class="param-label">Stimulation</span><span class="param-value">${formatBoolean(formData.stimulation)}</span></div>
          <div class="param-row"><span class="param-label">Scaling</span><span class="param-value">${formatBoolean(formData.scaling)}</span></div>
          <div class="param-row"><span class="param-label">Corrosion</span><span class="param-value">${formatBoolean(formData.corrosion)}</span></div>
        </div>
        <div>
          <div class="param-group-title">Construction</div>
          <div class="param-row"><span class="param-label">Circ. Temp</span><span class="param-value">${dv('circulatingTemp', formData.circulatingTemp)}</span></div>
          <div class="param-row"><span class="param-label">Press. Regime</span><span class="param-value">${dv('pressureRegime', formData.pressureRegime)}</span></div>
          <div class="param-row"><span class="param-label">UBO/MPD</span><span class="param-value">${formatBoolean(formData.ubompd)}</span></div>

          <div class="param-group-title" style="margin-top: 8px;">Drilling</div>
          <div class="param-row"><span class="param-label">UCS</span><span class="param-value">${dv('ucs', formData.ucs)}</span></div>
          <div class="param-row"><span class="param-label">Interbedded</span><span class="param-value">${formatBoolean(formData.interbedded)}</span></div>
          <div class="param-row"><span class="param-label">DDI</span><span class="param-value">${dv('ddi', formData.ddi)}</span></div>
          <div class="param-row"><span class="param-label">Multilateral</span><span class="param-value">${formatBoolean(formData.multilateral)}</span></div>
          <div class="param-row"><span class="param-label">Interception</span><span class="param-value">${formatBoolean(formData.interception)}</span></div>
        </div>
        <div>
          <div class="param-group-title">Well Control</div>
          <div class="param-row"><span class="param-label">Phase</span><span class="param-value">${dv('phase', formData.phase)}</span></div>
          <div class="param-row"><span class="param-label">Hydrocarbons</span><span class="param-value">${formatBoolean(formData.hydroCarbons)}</span></div>
          <div class="param-row"><span class="param-label">Toxic Gas</span><span class="param-value">${formatBoolean(formData.toxicGas)}</span></div>
        </div>
      </div>
    </div>

    <div class="footer">
      <div class="footer-left"><strong>IADC</strong> Geothermal Well Complexity Index</div>
      <div class="footer-right">${dateStr} | ${units === 'metric' ? 'Metric' : 'Imperial'} Units</div>
    </div>
  </div>
</body>
</html>`;
};

export const openPrintWindow = (htmlContent) => {
  const printWindow = window.open('', '_blank');
  if (printWindow) {
    printWindow.document.write(htmlContent);
    printWindow.document.close();
    printWindow.onload = () => {
      printWindow.print();
    };
  }
  return printWindow;
};

// Create URL-friendly slug from name
const slugify = (text) => {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
};

export const exportPDF = ({ id, name, scores, formData, units }) => {
  // If we have an assessment ID, use the dedicated print route (proper URL)
  if (id && name) {
    window.open(`/print/${id}/${slugify(name)}`, '_blank');
    return;
  }

  // For unsaved assessments (no ID), use inline HTML
  const html = generatePDFContent({ name: name || 'Unsaved Assessment', scores, formData, units });
  openPrintWindow(html);
};

// Download actual PDF file using html2canvas + jsPDF
export const downloadPDF = async ({ name, scores, formData, units }) => {
  const pageWidth = 794;
  const pageHeight = 1123;

  const html = generatePDFContent({ name: name || 'Unsaved Assessment', scores, formData, units });

  // Create hidden iframe
  const iframe = document.createElement('iframe');
  iframe.style.cssText = `position: fixed; left: -9999px; top: 0; width: ${pageWidth}px; height: ${pageHeight}px; border: none;`;
  document.body.appendChild(iframe);

  const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
  iframeDoc.open();
  iframeDoc.write(html);
  iframeDoc.close();

  // Wait for content to render
  await new Promise(resolve => {
    iframe.onload = resolve;
    setTimeout(resolve, 1000);
  });

  // Additional wait for fonts
  await new Promise(r => setTimeout(r, 500));

  try {
    const content = iframeDoc.querySelector('.page') || iframeDoc.body;

    const canvas = await html2canvas(content, {
      scale: 2,
      useCORS: true,
      logging: false,
      backgroundColor: '#ffffff',
      width: pageWidth,
      windowWidth: pageWidth
    });

    const imgData = canvas.toDataURL('image/png', 1.0);

    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4'
    });

    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();

    pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);

    const filename = `${(name || 'assessment').toLowerCase().replace(/[^a-z0-9]+/g, '-')}-complexity-index.pdf`;
    pdf.save(filename);

  } finally {
    document.body.removeChild(iframe);
  }
};
